import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import BudgetPage from './pages/BudgetPage';

import CostpointBudgetEditPage from './pages/CostpointBudgetEditPage';
import CostpointBudgetPage from './pages/CostpointBudgetPage';
import Dashboard from './pages/Dashboard';
import Forecast from './pages/Forecast';
import { ImportDraftPage } from './pages/ImportDraft';
import { LoginPage } from './pages/Login';
import { LoginFailedPage } from './pages/LoginFailed';
import { LoginSuccessPage } from './pages/LoginSuccess';
import MissingTags from './pages/MissingTags';
import Reports from './pages/Reports';
import Transactions from './pages/Transactions';

// Shared with LoginSuccessPage: the path an unauthenticated visitor originally tried to
// reach, saved here so they land back on it after completing the Steam OAuth round-trip
// rather than always landing on the generic default. sessionStorage (not router state)
// is required because that round-trip is a real browser navigation away to Steam and back,
// not an in-app route change - router state wouldn't survive it, but sessionStorage does,
// since it's tied to the browser tab, not any single page load.
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirectPath';

/**
 * Catches any path an unauthenticated visitor tries to reach that isn't one of the
 * explicit login-flow routes below (e.g. a bookmarked/shared link to /forecast, or any
 * other protected page) and sends them to /login instead of rendering nothing - previously
 * there was no catch-all here, so visiting a protected URL while logged out matched no
 * route in UnauthenticatedApp's <Routes> and silently rendered a blank page.
 */
function RedirectToLogin() {
  const location = useLocation();

  useEffect(() => {
    const attemptedPath = `${location.pathname}${location.search}`;
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, attemptedPath);
  }, [location]);

  return <Navigate to="/login" replace />;
}

function AuthenticatedApp() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/budget" element={<BudgetPage />} />

        <Route path="/budget/periods/:id/costpoints" element={<CostpointBudgetPage />} />
        <Route
          path="/budget/periods/:id/costpoints/:costpoint/edit"
          element={<CostpointBudgetEditPage />}
        />
        <Route path="/budgets" element={<BudgetPage />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/forecast" element={<Forecast />} />
        <Route path="/missing-tags" element={<MissingTags />} />
        <Route path="/import-draft/:draftId" element={<ImportDraftPage />} />
        <Route path="/login-failed" element={<LoginFailedPage />} />
      </Routes>
    </Layout>
  );
}

function UnauthenticatedApp() {
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login-success" element={<LoginSuccessPage />} />
      <Route path="/login-failed" element={<LoginFailedPage />} />
      <Route path="*" element={<RedirectToLogin />} />
    </Routes>
  );
}

function AppContent() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <AuthenticatedApp /> : <UnauthenticatedApp />;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </AuthProvider>
  );
}

export default App;
